import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ThumbsUp, ThumbsDown, X, Brain } from "lucide-react";
import { FeatureShell, DemoPanel } from "../components/sections/FeatureShell";
import { RECIPES } from "../data/site";

const NOTES = ["Kids loved it", "Too spicy", "Make it faster", "More veggies next time"];
const INITIAL = [
  { id: 1, text: "Maya prefers mild flavours" },
  { id: 2, text: "Weeknight dinners should be under 30 minutes" },
  { id: 3, text: "The family is nut-free" },
];

export default function Memory() {
  const [mem, setMem] = useState(INITIAL);
  const [votes, setVotes] = useState({});
  const remember = (text) => setMem((m) => [{ id: Date.now() + Math.random(), text }, ...m]);

  const vote = (r, up) => {
    setVotes((v) => ({ ...v, [r.name]: up }));
    remember(up ? `The family enjoyed ${r.name}` : `Suggest ${r.name} less often`);
  };

  return (
    <FeatureShell slug="feedback-memory">
      <DemoPanel title="Rate dinner, watch her remember" note="Demonstration only. This memory lives in this page and disappears when you leave — nothing is stored.">
        <div className="grid gap-8 lg:grid-cols-12">
          <div className="space-y-4 lg:col-span-7">
            <p className="eyebrow">This week’s dinners</p>
            {RECIPES.map((r, i) => (
              <div key={r.name} className="flex flex-col gap-4 rounded-2xl border border-cyan-400/20 bg-deep/60 p-4 sm:flex-row sm:items-center">
                <img src={r.img} alt={r.name} className="h-20 w-full rounded-xl object-cover sm:w-28" />
                <div className="flex-1">
                  <p className="font-display text-lg font-semibold text-white">{r.name}</p>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {NOTES.map((n) => <button key={n} onClick={() => remember(`${r.name}: ${n.toLowerCase()}`)} data-testid={`note-${i}-${n.toLowerCase().replace(/[^a-z]+/g, "-")}`} className="rounded-full border border-cyan-400/25 px-3 py-1 text-xs text-slate-200 transition-colors hover:border-cyan-300 hover:text-white">{n}</button>)}
                  </div>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => vote(r, true)} data-testid={`vote-up-${i}`} aria-label="Thumbs up" className={`grid h-11 w-11 place-items-center rounded-full border transition-colors ${votes[r.name] === true ? "border-cyan-300 bg-cyan-400 text-ink" : "border-cyan-400/40 text-cyan-200"}`}><ThumbsUp className="h-4 w-4" /></button>
                  <button onClick={() => vote(r, false)} data-testid={`vote-down-${i}`} aria-label="Thumbs down" className={`grid h-11 w-11 place-items-center rounded-full border transition-colors ${votes[r.name] === false ? "border-rose-300 bg-rose-400 text-ink" : "border-cyan-400/40 text-cyan-200"}`}><ThumbsDown className="h-4 w-4" /></button>
                </div>
              </div>
            ))}
          </div>
          <div className="lg:col-span-5">
            <div className="rounded-3xl border border-cyan-300/30 bg-gradient-to-br from-blue-950/70 to-deep p-6" data-testid="memory-log">
              <p className="eyebrow flex items-center gap-2"><Brain className="h-4 w-4" /> What Honesti would remember · {mem.length}</p>
              <ul className="mt-5 space-y-2">
                <AnimatePresence initial={false}>
                  {mem.map((m) => (
                    <motion.li key={m.id} layout initial={{ opacity: 0, y: -12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, x: 30 }} className="flex items-center justify-between gap-3 rounded-xl bg-white/5 px-4 py-3 text-sm text-slate-100">
                      {m.text}
                      <button onClick={() => setMem((l) => l.filter((x) => x.id !== m.id))} data-testid="forget-memory-btn" aria-label="Forget" className="rounded-full p-1 text-slate-300 hover:bg-white/10 hover:text-white"><X className="h-4 w-4" /></button>
                    </motion.li>
                  ))}
                </AnimatePresence>
              </ul>
              <p className="mt-5 text-xs text-slate-300">You would always be able to see, edit or forget anything Honesti remembers.</p>
            </div>
          </div>
        </div>
      </DemoPanel>
    </FeatureShell>
  );
}
